import React from 'react';

const GiftCard = ({ gift, isSelected, onToggle, selectedPeriod, onPeriodChange }) => {
  const { name, description, imageUrl, prices, code, giftLevel } = gift;

  const usdPrice = prices?.USD || 0;
  const bhdPrice = prices?.BHD || 0;

  // Transform payment period labels using Orders.jsx labels
  const transformPaymentPeriodLabel = (period) => {
    const labels = {
      'day1': 'Pay full within 2 days',
      'day30': 'Pay full by end of month',
      'day60': 'Pay full within 2 months'
    };
    return labels[period] || period;
  };

  // Check if this is a giftLevel product or a gift item
  const isGiftLevelProduct = giftLevel && Object.keys(giftLevel).length > 0;

  // Get available payment periods from giftLevel (only for giftLevel products)
  const availablePeriods = isGiftLevelProduct ? Object.keys(giftLevel).filter(key => 
    key.startsWith('day') && giftLevel[key] > 0
  ) : [];

  // Get budget for selected period (only for giftLevel products)
  const getCurrentBudget = () => {
    if (!isGiftLevelProduct || !selectedPeriod) return 0;
    return giftLevel[selectedPeriod] || 0;
  };

  return (
    <div className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden border ${isSelected ? 'border-[#19BFB7] ring-2 ring-[#19BFB7]/20' : 'border-gray-100'}`}>
      {/* Image */}
      <div className="relative h-48 bg-gray-100">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <span className="text-gray-400 text-4xl">🎁</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2" title={name}>
          {name}
        </h3>

        {description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2" title={description}>
            {description}
          </p>
        )}

        {/* Payment Period Selection - Only for giftLevel products */}
        {isGiftLevelProduct && availablePeriods.length > 0 && (
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Period
            </label>
            <select
              value={selectedPeriod || ''}
              onChange={(e) => onPeriodChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19BFB7] focus:border-[#19BFB7] text-sm"
            >
              <option value="">Select payment period</option>
              {availablePeriods.map(period => (
                <option key={period} value={period}>
                  {transformPaymentPeriodLabel(period)} ({getCurrentBudget()} BHD limit)
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Product Type Badge */}
        <div className="mb-2">
          <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
            isGiftLevelProduct 
              ? 'bg-blue-100 text-blue-800' 
              : 'bg-green-100 text-green-800'
          }`}>
            {isGiftLevelProduct ? 'Gift Level Product' : 'Gift Item'}
          </span>
        </div>

        {/* Prices */}
        <div className="flex justify-between items-center mb-2">
          <div className="text-sm">
            <span className="text-gray-500">USD:</span>
            <span className="font-semibold text-gray-800 ml-1">${usdPrice.toFixed(2)}</span>
          </div>
          <div className="text-sm">
            <span className="text-gray-500">BHD:</span>
            <span className="font-semibold text-gray-800 ml-1">{bhdPrice.toFixed(3)}</span>
          </div>
        </div>

        {/* Code */}
        {code && (
          <div className="text-xs text-gray-400 mt-2 mb-3">
            Code: {code}
          </div>
        )}

        {/* Add/Remove Button */}
        <button
          onClick={() => onToggle(gift)}
          disabled={isGiftLevelProduct && !selectedPeriod}
          className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
            isSelected
              ? 'bg-red-500 hover:bg-red-600 text-white'
              : isGiftLevelProduct && !selectedPeriod
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-[#19BFB7] hover:bg-[#158a85] text-white'
          }`}
        >
          {isSelected ? 'Remove' : 
           isGiftLevelProduct ? 
             (selectedPeriod ? 'Select Product' : 'Select Period First') : 
             'Add Gift'
          }
        </button>
      </div>
    </div>
  );
};

export default GiftCard;
